import { useEffect, useState } from "react";
import axios from "axios";
import { Inbox, ArrowLeft, Users, Mail } from "lucide-react";
import { Logo } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
    const [tab, setTab] = useState("enquiries");
    const [enquiries, setEnquiries] = useState(null);
    const [applications, setApplications] = useState(null);

    useEffect(() => {
        document.title = "Inbox, Syntera Technologies";
        axios.get(`${API}/enquiries`).then((r) => setEnquiries(r.data)).catch(() => setEnquiries([]));
        axios.get(`${API}/applications`).then((r) => setApplications(r.data)).catch(() => setApplications([]));
    }, []);

    const loading = enquiries === null || applications === null;

    return (
        <div className="min-h-screen bg-ink px-5 py-10 text-slate-100 sm:px-8" data-testid="admin-enquiries-page">
            <div className="mx-auto max-w-5xl">
                <div className="flex items-center justify-between">
                    <Logo />
                    <a href="/" data-testid="admin-back-link" className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-slate-400 transition-colors hover:text-volt">
                        <ArrowLeft size={14} /> Back to site
                    </a>
                </div>

                <h1 className="mt-10 font-syne text-3xl font-bold text-white" data-testid="admin-heading">
                    Inbox
                </h1>
                <p className="mt-2 text-sm text-slate-400">Messages and job applications submitted through the website.</p>

                <div className="mt-8 flex gap-2" data-testid="admin-tabs">
                    <button
                        data-testid="admin-tab-enquiries"
                        onClick={() => setTab("enquiries")}
                        className={`flex items-center gap-2 rounded-full border px-5 py-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] transition-all duration-300 ${
                            tab === "enquiries" ? "border-volt/60 bg-volt/10 text-volt" : "border-white/10 text-slate-400 hover:text-white"
                        }`}
                    >
                        <Mail size={14} /> Enquiries {enquiries ? `(${enquiries.length})` : ""}
                    </button>
                    <button
                        data-testid="admin-tab-applications"
                        onClick={() => setTab("applications")}
                        className={`flex items-center gap-2 rounded-full border px-5 py-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] transition-all duration-300 ${
                            tab === "applications" ? "border-volt/60 bg-volt/10 text-volt" : "border-white/10 text-slate-400 hover:text-white"
                        }`}
                    >
                        <Users size={14} /> Applications {applications ? `(${applications.length})` : ""}
                    </button>
                </div>

                {loading ? (
                    <p className="mt-10 text-slate-500" data-testid="admin-loading">Loading…</p>
                ) : tab === "enquiries" ? (
                    enquiries.length === 0 ? (
                        <div className="mt-10 flex flex-col items-center gap-3 rounded-2xl border border-white/[0.08] bg-card2/50 py-16 text-slate-500" data-testid="admin-empty-state">
                            <Inbox size={32} />
                            <p>No enquiries yet.</p>
                        </div>
                    ) : (
                        <div className="mt-8 space-y-4">
                            {enquiries.map((e) => (
                                <div key={e.id} className="rounded-2xl border border-white/[0.08] bg-card2/50 p-6" data-testid={`admin-enquiry-${e.id}`}>
                                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                                        <div className="font-syne text-lg font-semibold text-white">
                                            {e.name}
                                            {e.company ? <span className="ml-2 text-sm font-normal text-slate-400">· {e.company}</span> : null}
                                        </div>
                                        <div className="font-mono text-[11px] text-slate-500">
                                            {new Date(e.created_at).toLocaleString()}
                                        </div>
                                    </div>
                                    <div className="mt-1 font-mono text-xs text-volt">{e.email}</div>
                                    {e.service ? (
                                        <div className="mt-2 inline-block rounded-full border border-volt/25 bg-volt/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-volt">
                                            {e.service}
                                        </div>
                                    ) : null}
                                    <p className="mt-3 text-sm leading-relaxed text-slate-300">{e.message}</p>
                                </div>
                            ))}
                        </div>
                    )
                ) : applications.length === 0 ? (
                    <div className="mt-10 flex flex-col items-center gap-3 rounded-2xl border border-white/[0.08] bg-card2/50 py-16 text-slate-500" data-testid="admin-empty-applications">
                        <Inbox size={32} />
                        <p>No applications yet.</p>
                    </div>
                ) : (
                    <div className="mt-8 space-y-4">
                        {applications.map((a) => (
                            <div key={a.id} className="rounded-2xl border border-white/[0.08] bg-card2/50 p-6" data-testid={`admin-application-${a.id}`}>
                                <div className="flex flex-wrap items-baseline justify-between gap-2">
                                    <div className="font-syne text-lg font-semibold text-white">{a.name}</div>
                                    <div className="font-mono text-[11px] text-slate-500">
                                        {new Date(a.created_at).toLocaleString()}
                                    </div>
                                </div>
                                <div className="mt-1 font-mono text-xs text-volt">
                                    {a.email}{a.phone ? ` · ${a.phone}` : ""}
                                </div>
                                <div className="mt-2 inline-block rounded-full border border-volt/25 bg-volt/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-volt">
                                    {a.role}
                                </div>
                                {a.resume_link ? (
                                    <div className="mt-2">
                                        <a href={a.resume_link} target="_blank" rel="noopener noreferrer" className="font-mono text-xs text-slate-300 underline decoration-volt/40 underline-offset-4 transition-colors hover:text-volt" data-testid={`admin-resume-${a.id}`}>
                                            View resume / portfolio
                                        </a>
                                    </div>
                                ) : null}
                                {a.message ? <p className="mt-3 text-sm leading-relaxed text-slate-300">{a.message}</p> : null}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
